import React, { useEffect } from "react";
import qs from "qs";
import { useTranslation, Trans } from "@lib/i18n";
import { string, bool, number, func } from "prop-types";

import RequestButton from "@components/shared/RequestButton";
import CollapsableContainer from "@components/shared/CollapsableContainer";
import Typography from "@components/shared/Typography";

import { basketPersonalContinueEcommerceEvent } from "@gtm/events/basket";
import { getBrandIdLoginRedirect } from "@lib/getBrandIdLoginRedirect";
import { toast } from "@lib/reactToastify";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import gtm from "@gtm/core";
import { useBrandIdSettingsSelector } from "@redux/reducers/appConfig";
import { redirectToExternalURL } from "@shared/v2/lib/redirectToExternalURL";

const RedirectBrandIdForms = ({
  containerOnChange,
  defaultOpen,
  disabled,
  gtmEvent,
  index,
  isHeadTextHidden = false,
  isOpen,
  redirectLogin,
  redirectRegistration,
  registrationType = null,
  ...restProps
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const isB2BSelected = useIsB2BSelected();
  const brandIDConfig = useBrandIdSettingsSelector();

  useEffect(() => {
    if (isOpen && gtmEvent) {
      gtmEvent();
    }
  }, [isOpen]);

  const onRegisterButtonClick = async () => {
    let brandIdQuery = {
      location: redirectRegistration,
    };

    if (registrationType) {
      gtm.fire(
        basketPersonalContinueEcommerceEvent({
          personalOption: "Create account",
          isB2BSelected,
        })
      );

      brandIdQuery.creation = registrationType;
    }

    window.location.href = `${
      brandIDConfig.dealApiUrl
    }/user/register${qs.stringify(brandIdQuery, {
      addQueryPrefix: true,
      skipNulls: true,
      encode: false,
    })}`;
  };

  const onLoginButtonClick = () => {
    if (registrationType) {
      gtm.fire(
        basketPersonalContinueEcommerceEvent({
          personalOption: "Login",
          isB2BSelected,
        })
      );
    }

    return redirectToExternalURL(
      getBrandIdLoginRedirect(redirectLogin, brandIDConfig, language)
    );
  };

  const onRequestError = error => {
    toast.error(
      t("notify.apiDefaultError"),
      {
        toastId: "brandid-registration-redirect-error",
      },
      error
    );
  };

  return (
    <CollapsableContainer
      defaultOpen={defaultOpen}
      isDisabled={disabled}
      isHeadTextHidden={isHeadTextHidden}
      isOpen={isOpen}
      onChange={containerOnChange}
      title={t("basket.personalDetails.selectTitle")}
      titleIndex={index}
      {...restProps}
    >
      <div className="flex flex-col flex-wrap justify-around items-center w-full gap-4 pb-1 sm:justify-between">
        <Typography className="text-sm">
          <Trans i18nKey="myDetails.redirectContainer.loginCreateAccount" />
        </Typography>
        <Typography className="text-sm mb-8">
          {t("myDetails.redirectContainer.existingAccount")}
        </Typography>
        <RequestButton
          data-id="basket-redirect-registration-button"
          triggerRequestCallback={onRegisterButtonClick}
          onRequestError={onRequestError}
        >
          {t("myDetails.redirectContainer.register")}
        </RequestButton>
        <RequestButton
          data-id="basket-redirect-login-button"
          triggerRequestCallback={onLoginButtonClick}
        >
          {t("myDetails.redirectContainer.login")}
        </RequestButton>
      </div>
    </CollapsableContainer>
  );
};

RedirectBrandIdForms.propTypes = {
  disabled: bool.isRequired,
  redirectLogin: string.isRequired,
  redirectRegistration: string.isRequired,
  containerOnChange: func,
  defaultOpen: bool,
  gtmEvent: func,
  index: number,
  inverted: bool,
  isCurrentStep: bool,
  isHeadTextHidden: bool,
  isOpen: bool,
  registrationType: number,
};

export default RedirectBrandIdForms;
